import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import usersApi from "@/api/usersApi";
import UserProfileResponse from "@/model/UserProfileResponse";
import roleHomePath from "@/routing/roleHomePath";
import ChangePasswordForm from "@/components/ChangePasswordForm/ChangePasswordForm";
import "@/pages/ProfilePage/ProfilePage.scss";

function ProfilePage() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    usersApi
      .getCurrentUser(session.accessToken)
      .then((result) => {
        if (!cancelled) {
          setProfile(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!session) {
    return null;
  }

  return (
    <div className="profile-page">
      <h1>{t("users.profile.title")}</h1>

      {isLoading && <p>{t("users.profile.loading")}</p>}
      {!isLoading && error && (
        <p role="alert">{t("users.profile.error")}</p>
      )}

      {!isLoading && !error && profile && (
        <dl className="profile-page__details">
          <dt>{t("users.profile.firstNameLabel")}</dt>
          <dd>{profile.firstName ?? "—"}</dd>

          <dt>{t("users.profile.lastNameLabel")}</dt>
          <dd>{profile.lastName ?? "—"}</dd>

          <dt>{t("users.profile.emailLabel")}</dt>
          <dd>{profile.email ?? "—"}</dd>

          <dt>{t("users.profile.roleLabel")}</dt>
          <dd>
            {profile.role ? t(`users.create.roles.${profile.role}`) : "—"}
          </dd>

          {profile.companyId && (
            <>
              <dt>{t("users.profile.companyIdLabel")}</dt>
              <dd>{profile.companyId}</dd>
            </>
          )}
        </dl>
      )}

      <ChangePasswordForm />

      <Link to={roleHomePath(session.role)}>{t("users.profile.backLink")}</Link>
    </div>
  );
}

export default ProfilePage;
